import { useCallback, useState } from 'react';
import { Modal as RNModal, StyleSheet, View } from 'react-native';
import { useParams } from 'react-router';

import { useViewStudentDataScreensFeature } from '~/featureFlags';
import { MainLayout } from '~/layouts/MainLayout';
import { ReviewPeriod } from '~/pages/review';
import { RosterScreen } from '~/screens/roster';
import { isPendingSessionStatus, SessionStatus, Period } from '~/services/types';
import { usePeriodListAllQuery } from '~/services/period';
import { ErrorBox } from '~/components/ErrorBox';

import { ProcessingBanner } from './ui/ProcessingBanner';

export function Review() {
  const { periodShortName } = useParams();
  const { data, error: periodListAllError } = usePeriodListAllQuery();
  const { periods } = data || {};

  const activePeriod = periods?.find((period: Period) => period.shortName === periodShortName) || periods?.[0];

  const isViewStudentDataScreensEnabled = useViewStudentDataScreensFeature();

  const [processingStatus, setProcessingStatus] = useState<SessionStatus | null>(null);
  const [date, setDate] = useState(new Date());
  const [rosterModalOpen, setRosterModalOpen] = useState(false);

  const onSessionStatusChange = useCallback(
    (status?: SessionStatus) => {
      if (isPendingSessionStatus(status)) {
        setProcessingStatus(status);
      } else {
        setProcessingStatus(null);
      }
    },
    [setProcessingStatus],
  );

  let content: React.ReactNode;
  if (periodListAllError) {
    content = (
      <ErrorBox
        text="Could not load period data. Please try refreshing the page."
        containerStyle={{ borderRadius: 0 }}
      />
    );
  } else if (!activePeriod) {
    content = (
      <ErrorBox
        text="No period selected. Please select a period from the sidebar."
        containerStyle={{ borderRadius: 0 }}
      />
    );
  } else {
    content = (
      <ReviewPeriod
        key={activePeriod.id}
        activePeriod={activePeriod}
        date={date}
        onSessionStatusChange={onSessionStatusChange}
        onRosterPress={() => setRosterModalOpen(true)}
        setDate={setDate}
      />
    );
  }

  return (
    <MainLayout>
      <View style={styles.stack}>
        {processingStatus && <ProcessingBanner status={processingStatus} />}
        <View style={styles.content}>{content}</View>
      </View>
      {isViewStudentDataScreensEnabled && (
        <RNModal visible={rosterModalOpen} onRequestClose={() => setRosterModalOpen(false)} animationType="none">
          <RosterScreen activePeriod={activePeriod} onClose={() => setRosterModalOpen(false)} />
        </RNModal>
      )}
    </MainLayout>
  );
}

const styles = StyleSheet.create({
  stack: {
    height: '100%',
    width: '100%',
  },
  content: {
    flex: 1,
  },
});
